import { graphql } from 'gatsby';
import { useRef } from 'react';
import errorLogger from '@/utils/errorLogger';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import Input from '../components/Input/Input';
import Select from '../components/Select/Select';
import FormError from '../components/FormError/FormError';
import LegalText, { type LegalTextProps } from '../components/LegalText/LegalText';
import pushInDataLayer from '@/utils/pushInDataLayer';
import { type ReCAPTCHA } from 'react-google-recaptcha';
import GoogleRecaptcha from '../components/GoogleRecaptcha/GoogleRecaptcha';
import { isTokenRecaptchaValid } from '@/utils/validateGoogleRecaptchaToken';
import getCheqHiddenFieldValue from '../components/CheqHiddenField/getCheqHiddenFieldValue';
import CheqHiddenField from '../components/CheqHiddenField/CheqHiddenField';
import MarketingOptOut from '../components/MarketingOptOut/MarketingOptOut';
import { newsletterFormSchema } from './validation';
import { FormFooter, StyledForm, SubmitButton } from './NewsletterForm.styles';
import { getItemsFromList, type listItem } from '@/utils/getItemsFromList';
import useSetGeolocatedCountry from '../hooks/useSetGeolocatedCountry';
import countryList from '../constants/countryList';

interface NewsletterFormProps extends Queries.NEWSLETTER_FORMFragment {
  onSubmitSuccess?: () => void;
  submitLabel?: string | null;
}

const NewsletterForm = ({
  newsletterFormElements: elements,
  onSubmitSuccess,
  submitLabel,
}: NewsletterFormProps) => {
  const { campaign_id, product, languages, ides, options, legal_text } = elements;
  const productId = product.value;
  const hideLabel = options?.value?.includes('hide_labels') ?? false;
  const languageList = getItemsFromList(
    languages?.value?.languageListElements.languages.value as listItem[]
  );
  const ideList = getItemsFromList(ides?.value?.ideListElements.ides.value as listItem[]);
  const hasIdesFormField = Boolean(ideList?.length);
  const hasLanguagesFormField = Boolean(languageList?.length);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { t } = useTranslation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<typeof newsletterFormSchema>({
    mode: 'onBlur',
    resolver: zodResolver(newsletterFormSchema),
    defaultValues: {
      email: '',
      ides: hasIdesFormField ? [] : undefined,
      languages: hasLanguagesFormField ? [] : undefined,
      form_type: 'newsletter_form',
      product: productId,
      campaign_id: campaign_id?.value || '',
      country: null,
    },
  });

  useSetGeolocatedCountry({ setValue, field: 'country', countryList });

  const onSubmit = handleSubmit(async (formValues) => {
    const token = await recaptchaRef.current?.executeAsync();
    await isTokenRecaptchaValid(token ?? null, 'subscription-form');
    const cq_req_id = getCheqHiddenFieldValue();
    const country = formValues.country;
    const requestBody = {
      ...formValues,
      cq_req_id, // Cheq Request ID
      country,
    };

    try {
      const response = await fetch('/api/submit-form', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      reset();

      pushInDataLayer({
        event: 'submittedForm',
        submittedFormData: {
          type: 'registration',
          email: formValues.email,
          country: formValues.country,
          product: productId,
          ...(hasIdesFormField && formValues.ides?.length && { ides: formValues.ides }),
          ...(hasLanguagesFormField &&
            formValues.languages?.length && { languages: formValues.languages }),
        },
      });

      onSubmitSuccess?.();
    } catch (error) {
      errorLogger(error);
      setError('root', { type: 'custom', message: t('form.Could not submit form') });
    }
  });

  return (
    <StyledForm noValidate onSubmit={onSubmit}>
      {hasIdesFormField && (
        <Controller
          control={control}
          name="ides"
          render={({ field }) => (
            <Select
              required
              id="ides"
              isMulti
              placeholder={t('newsletterForm.Select your preferred IDE')}
              options={ideList?.map((ide) => ({ label: ide, value: ide }))}
              formGroupProps={{
                label: t('newsletterForm.Your favorite IDEs'),
                hideLabel,
              }}
              errors={errors}
              {...field}
            />
          )}
        />
      )}
      {hasLanguagesFormField && (
        <Controller
          control={control}
          name="languages"
          render={({ field }) => (
            <Select
              required
              id="languages"
              isMulti
              placeholder={t('newsletterForm.Select your preferred languages')}
              options={languageList?.map((language) => ({ label: language, value: language }))}
              formGroupProps={{ label: t('newsletterForm.Your favorite languages'), hideLabel }}
              errors={errors}
              {...field}
            />
          )}
        />
      )}
      <Input
        type="email"
        id="email"
        required
        placeholder={t('form.Email')}
        formGroupProps={{ label: t('form.Email'), hideLabel }}
        {...register('email')}
        errors={errors}
      />
      <MarketingOptOut
        label={t('form.Marketing Emails Opt Out')}
        errors={errors}
        register={register}
        productId={productId}
      />
      <input type="hidden" {...register('form_type')} />
      <input type="hidden" {...register('product')} />
      <input type="hidden" {...register('campaign_id')} />
      <input type="hidden" {...register('country')} />
      <CheqHiddenField />
      <FormFooter>
        <SubmitButton type="submit" disabled={isSubmitting} variant="primary">
          {isSubmitting ? t('form.Loading') : submitLabel || t('newsletterForm.Sign up')}
        </SubmitButton>
        <LegalText richText={legal_text as LegalTextProps['richText']} />
        {errors?.root?.message && <FormError>{errors.root.message}</FormError>}
      </FormFooter>
      <GoogleRecaptcha ref={recaptchaRef} />
    </StyledForm>
  );
};

export const fragment = graphql`
  fragment NEWSLETTER_FORM on kontent_item_newsletter_form {
    __typename
    newsletterFormElements: value {
      campaign_id {
        value
      }
      product: products__product {
        value
      }
      languages {
        value {
          languageListElements: elements {
            languages {
              value {
                ... on kontent_item_language_item {
                  elements {
                    name {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
      ides {
        value {
          ideListElements: elements {
            ides {
              value {
                ... on kontent_item_ide_item {
                  elements {
                    name {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
      legal_text {
        value
        links {
          link_id
          codename
        }
      }
      options {
        value
      }
    }
  }
`;

export default NewsletterForm;
